import React, { useEffect, useRef, useState } from "react";

import type { ColDef, GetRowIdParams, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
  Alert,
  Button,
  Divider,
  Group,
  Box,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch as MantineSwitch,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { IOtherAttributesFormula, IOtherAttributesFormulaConfItem } from "@schemas/otherAttributesSchema";
import { useHistory } from "react-router-dom";
import { Card, CardBody, CardTitle } from "reactstrap";
import { useImmer } from "use-immer";
import { useGetSingleIssueBoardQuery } from "../../../app/routes/issuesApp/IssueBoardRTK";
import { ISSUE_COLUMN_LABELS, ISSUE_DATAFILEDS_ENUM } from "../../../app/routes/issuesApp/issueConstants";
import { getIssueBoardIdFromUrl } from "../../../app/routes/issuesApp/issueUtils";
import { getProjectIdFromUrl } from "../../../app/utils";
import { HSpace, McTitle, VSpace } from "../../index";
import "./styles.css";
import Switch from "react-switch";

const rowClassRules = {
  "green-row": 'data.type == "DELIMITER"',
  "blue-row": 'data.type == "ATTRIBUTE"',
  "counter-row": 'data.type == "COUNTER"',
};

const defaultColDef: ColDef = {
  flex: 1,
  minWidth: 100,
  filter: true,
};

const FormulaConfig: React.FC<{
  formula: IOtherAttributesFormula;
  onFormulaConfUpdate: (formula: IOtherAttributesFormula) => void;
}> = ({ formula, onFormulaConfUpdate }) => {
  const [leftApi, setLeftApi] = useState<GridApi | null>(null);
  const [rightApi, setRightApi] = useState<GridApi | null>(null);
  const [leftRowData, setLeftRowData] = useImmer<IOtherAttributesFormulaConfItem[]>([]);
  const [rightRowData, setRightRowData] = useImmer<IOtherAttributesFormulaConfItem[]>([]);

  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const issueBoardId = getIssueBoardIdFromUrl(history.location.pathname);

  const { data: issueBoard } = useGetSingleIssueBoardQuery({
    projectId: projectId,
    issueBoardId: issueBoardId,
  });

  const otherAttributesConfig = issueBoard.otherAttributesConfig;

  const leftColumns: ColDef<IOtherAttributesFormulaConfItem>[] = [
    { field: "id", hide: true },
    {
      rowDrag: true,
      field: "value",
      headerName: "Attribut",
      width: 200,
      cellRenderer: (params) => {
        return getAttributeLabel(params.value);
      },
    },
  ];

  const RightColumns: ColDef<IOtherAttributesFormulaConfItem>[] = [
    { field: "id", hide: true },
    { field: "type", hide: true },
    {
      rowDrag: true,
      field: "value",
      filter: false,
      headerName: "Attribut",
      cellRenderer: (params) => {
        const rowId = params.data.id;
        const label =
          params.data.type == "COUNTER"
            ? "Löpnummer"
            : params.data.type === "DELIMITER"
            ? params.value
            : getAttributeLabel(params.value);
        return (
          <span>
            <i
              style={{ cursor: "pointer" }}
              onClick={() =>
                onFormulaConfUpdate({
                  ...formula,
                  confArr: rightRowData.filter((item) => item.id !== rowId),
                })
              }
              className="fa fa-trash fa-fw"
            />
            {label}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    const items = [
      {
        id: ISSUE_DATAFILEDS_ENUM.BUCKET as string,
        type: "ATTRIBUTE" as "ATTRIBUTE",
        value: ISSUE_DATAFILEDS_ENUM.BUCKET,
      },
      {
        id: ISSUE_DATAFILEDS_ENUM.STATUS,
        type: "ATTRIBUTE" as "ATTRIBUTE",
        value: ISSUE_DATAFILEDS_ENUM.STATUS,
      },
      {
        id: ISSUE_DATAFILEDS_ENUM.TITLE,
        type: "ATTRIBUTE" as "ATTRIBUTE",
        value: ISSUE_DATAFILEDS_ENUM.TITLE,
      },
    ];
    setLeftRowData(items);
    const attributeItems = Object.entries(otherAttributesConfig || {}).reduce<IOtherAttributesFormulaConfItem[]>(
      (acc, [key, value]) => {
        if (value.typeOfValue !== "FORMULA") {
          const item: IOtherAttributesFormulaConfItem = {
            id: key,
            type: "ATTRIBUTE",
            value: key,
          };
          acc.push(item);
        }
        return acc;
      },
      [],
    );
    setLeftRowData((draft) => {
      draft.push(...attributeItems);
    });
  }, []);

  useEffect(() => {
    let _formula: IOtherAttributesFormula = null;
    if (formula == null) {
      _formula = {
        nrOfCharsInCounter: 3,
        confArr: [],
      };
    } else {
      _formula = formula;
    }
    const rows = (_formula.confArr ?? []).map((item) => {
      return {
        id: item.id ?? Math.random().toString(36).substring(7),
        type: item.type,
        value: item.value,
      };
    });
    setRightRowData(rows);
  }, [formula]);

  const eLeftGrid = useRef(null);
  const eRightGrid = useRef(null);

  const getRowId = (params: GetRowIdParams) => String(params.data.id);

  const addGridDropZone = (side: string, api: GridApi) => {
    const dropApi = side === "Left" ? rightApi : leftApi;
    const dropZone = dropApi!.getRowDropZoneParams();

    api.addRowDropZone(dropZone);
  };

  useEffect(() => {
    if (rightApi && leftApi) {
      addGridDropZone("Right", rightApi);
      addGridDropZone("Left", leftApi);
    }
  });

  const onGridReady = (side: string, params: GridReadyEvent) => {
    if (side === "Left") {
      setLeftApi(params.api);
    } else {
      setRightApi(params.api);
    }
  };

  const onRowDragEnd = (grid: string, event: any) => {
    if (grid === "Left") {
      return;
    }
    const rows = event.api?.getRenderedNodes();
    onFormulaConfUpdate({
      ...formula,
      confArr: rows.map((row) => {
        return {
          id: row.data.id,
          type: row.data.type,
          value: row.data.value,
        };
      }),
    });
  };

  const getInnerGridCol = (side: string) => (
    <div className="inner-col">
      <div style={{ height: "100%" }} className="inner-col" ref={side === "Left" ? eLeftGrid : eRightGrid}>
        <AgGridReact
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          rowClassRules={rowClassRules}
          rowDragManaged={true}
          suppressMoveWhenRowDragging={true}
          readOnlyEdit={true}
          onRowDragEnd={(event) => onRowDragEnd(side, event)}
          rowData={side === "Left" ? leftRowData ?? [] : rightRowData ?? []}
          columnDefs={side === "Left" ? leftColumns : RightColumns}
          onGridReady={(params) => onGridReady(side, params)}
        />
      </div>
    </div>
  );

  const addOwnDelimiter = (str: string) => {
    onFormulaConfUpdate({
      ...formula,
      confArr: [
        ...formula.confArr,
        {
          id: Math.random().toString(36).substring(7),
          type: "DELIMITER",
          value: str,
        },
      ],
    });
    setInputValue("");
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      addOwnDelimiter(inputValue);
      setInputValue("");
      return;
    }
  };

  const [inputValue, setInputValue] = useState("");

  const addCounter = () => {
    if (rightRowData.find((row) => row.type === "COUNTER")) {
      return;
    }
    onFormulaConfUpdate({
      ...formula,
      confArr: [
        ...formula.confArr,
        {
          id: Math.random().toString(36).substring(7),
          type: "COUNTER",
          value: "Counter",
        },
      ],
    });
  };

  const counterIsAdded = rightRowData.find((row) => row.type === "COUNTER") != null;

  const generatedSting = rightRowData
    .map((row) => {
      if (row.type === "ATTRIBUTE") {
        return ISSUE_COLUMN_LABELS[row.value as ISSUE_DATAFILEDS_ENUM]?.toUpperCase() || row.value;
      }
      if (row.type === "COUNTER") {
        return getLabelForCounter(formula.nrOfCharsInCounter);
      }
      return row.value;
    })
    .join("");

  const getAttributeLabel = (value: string) => {
    const label = ISSUE_COLUMN_LABELS[value as ISSUE_DATAFILEDS_ENUM];
    if (label) {
      return label;
    }
    const otherAttribute = otherAttributesConfig[value];
    if (otherAttribute) {
      return `(${value}) ${otherAttribute.label}`;
    }
  };

  const nrOfCharsOptions = [
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];

  return (
    <>
      <VSpace />
      <Card className="bg-light h-100 w-100">
        <CardBody>
          <CardTitle tag="h5">Konfigurasjon for formell</CardTitle>
          <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
            <div className="d-flex flex-column h-100">
              <VSpace />
              <div className="bg-warning p-2 border-radius">
                <VSpace />
                <h6>{generatedSting}</h6>
              </div>
              <VSpace />
              <div className="d-flex h-100 w-100">
                {getInnerGridCol("Left")}
                <HSpace />
                <div className="d-flex flex-column h-100">
                  <Box>
                    <Text fw={600} mb={6}>
                      Skriv inn tegn
                    </Text>
                    <TextInput
                      autoFocus
                      value={inputValue}
                      placeholder="Legg til valg"
                      styles={{
                        input: {
                          backgroundColor: "#c7e8c5",
                          maxWidth: 220,
                        },
                      }}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={blurOnEnter}
                      onBlur={(e) => {
                        if (e.target.value.length > 0) {
                          setInputValue("");
                        }
                      }}
                    />
                  </Box>
                  <Divider label="Löpnummer" labelPosition="center" my="sm" />
                  <Button
                    color="orange"
                    variant="light"
                    disabled={counterIsAdded}
                    leftSection={<i className="fa fa-list-ol fa-fw" />}
                    onClick={() => addCounter()}
                  >
                    {counterIsAdded ? "Löpnummer lagt til" : "Legg til löpnummer"}
                  </Button>
                </div>
                <HSpace />
                {getInnerGridCol("Right")}
              </div>
            </div>
          </div>
          <hr />
          {counterIsAdded && (
            <Paper withBorder p="md" radius="sm" mb="md" bg="white" style={{ borderColor: "#f08c0066" }}>
              <Stack gap="sm">
                <Group gap="xs">
                  <ThemeIcon color="orange" variant="light" radius="sm">
                    <i className="fa fa-list-ol fa-fw" />
                  </ThemeIcon>
                  <Text fw={600}>Löpnummer</Text>
                </Group>
                <Box>
                  <Text size="sm" fw={600} mb={6}>
                    Antall tegn i löpnummer
                  </Text>
                  <Select
                    aria-label="Antall tegn i löpnummer"
                    value={String(formula.nrOfCharsInCounter ?? 3)}
                    onChange={(value) => {
                      const nrOfCharsInCounter = Number(value);
                      if (Number.isNaN(nrOfCharsInCounter)) {
                        return;
                      }

                      onFormulaConfUpdate({
                        ...formula,
                        nrOfCharsInCounter,
                      });
                    }}
                    data={nrOfCharsOptions.map((option) => ({
                      value: String(option.value),
                      label: option.label,
                    }))}
                  />
                  <Text size="sm" c="dimmed" mt={6}>
                    Bestemmer minimumslengden på nummeret. Eksempel: 3 tegn gir 001, 002 og 101. 4 tegn gir 0001 og
                    0002.
                  </Text>
                </Box>
                <SegmentedControl
                  color="orange"
                  value={formula.counterMode ?? "AUTO"}
                  onChange={(value) =>
                    onFormulaConfUpdate({
                      ...formula,
                      counterMode: value as IOtherAttributesFormula["counterMode"],
                      manualCounter: {
                        enforceUniqueWithinGroup: formula.manualCounter?.enforceUniqueWithinGroup ?? true,
                      },
                    })
                  }
                  data={[
                    { value: "AUTO", label: "Automatisk" },
                    { value: "MANUAL", label: "Manuell" },
                  ]}
                />
                {(formula.counterMode ?? "AUTO") === "AUTO" ? (
                  <>
                    <Alert color="blue">
                      Löpnummer beregnes automatisk i kronologisk ordning innen hver formelgruppe. Rekkefølgen følger
                      når sakene ble opprettet.
                    </Alert>
                  </>
                ) : (
                  <>
                    <Alert color="yellow">
                      Brukeren angir løpnummeret selv. Systemet kan fortsatt bruke samme formel til å foreslå neste
                      ledige nummer.
                    </Alert>
                    <Group>
                      <MantineSwitch
                        checked={formula.manualCounter?.enforceUniqueWithinGroup !== false}
                        onChange={(event) =>
                          onFormulaConfUpdate({
                            ...formula,
                            manualCounter: {
                              enforceUniqueWithinGroup: event.currentTarget.checked,
                            },
                          })
                        }
                        label="Krev unikt nummer innen samme formelgruppe"
                      />
                    </Group>
                  </>
                )}
              </Stack>
            </Paper>
          )}
          <McTitle title="Fjern foregående tegnsetting hvis attributtet ikke er angitt">
            <Switch
              checked={formula.deletePreviousDelimiterIfAttributeIsEmpty || false}
              onChange={(bool) =>
                onFormulaConfUpdate({
                  ...formula,
                  deletePreviousDelimiterIfAttributeIsEmpty: bool,
                })
              }
            />{" "}
          </McTitle>
        </CardBody>
      </Card>
    </>
  );
};

export default FormulaConfig;

const getLabelForCounter = (nrOfChars: number): string => {
  // nr of chars = 3 => "001", nr of chars = 4 => "0001", nr of chars = 5 => "00001", etc
  if (nrOfChars === 3) {
    return "001";
  }
  if (nrOfChars === 4) {
    return "0001";
  }
  if (nrOfChars === 2) {
    return "01";
  }
};
